import { makeAutoObservable } from "mobx";

/**
 * Numeric limits the builder enforces while an automation is being edited.
 * Every one is a UCES `number` flag owned by the automation engine (see its
 * flags.yml), which enforces the same values on save, so a limit can be raised
 * or lowered per account/plan without a release.
 *
 * The engine also caps loop nesting at 2 whatever the flags say.
 */
export interface AutomationLimits {
  /**
   * Steps per automation, counting every action plus each Conditional Logic /
   * Looping block -- gate on candidate.totalStepCount, not allActions.length.
   */
  readonly maxActionsPerAutomation: number;
  readonly maxAutomationsPerSolution: number;
  /** Conditions on a single trigger, action or branch. */
  readonly maxTriggerConditions: number;
  readonly maxChecklistItems: number;
  /**
   * Branches in one Conditional Logic block. Everything that grows the block
   * -- `addBranch`, the Add Branch button and the save validation -- counts
   * the else-branch toward it (spec 2.3: the else-branch is the block's last
   * branch). The one exception is `setBranchMode`'s convert-to-conditions
   * gate, which compares `branches[]` alone because converting the
   * else-branch empties the else slot as the array grows, leaving the total
   * unchanged.
   */
  readonly maxConditionsBranches: number;
  /**
   * Branches a Conditional Logic block must keep, counting conditions branches
   * *and* the else-branch together: it's what makes "the last branch can't be
   * deleted" hold whichever kind that last branch is.
   */
  readonly minTotalBranches: number;
  /**
   * Nested Advanced Logic levels allowed on a path, counting Conditional Logic
   * and Looping jointly -- at 2, any 3rd level (CL->Loop->CL,
   * Loop->CL->Loop, ...) is blocked.
   */
  readonly maxAdvancedLogicDepth: number;
}

/**
 * The values used until the flags load, and if loading fails. Also the
 * `default:` values in the engine's flags.yml -- keep the two in step.
 * Frozen: it doubles as the shared fallback every `useUcesFlag` call passes.
 */
export const DEFAULT_AUTOMATION_LIMITS: AutomationLimits = Object.freeze({
  maxActionsPerAutomation: 20,
  maxAutomationsPerSolution: 100000,
  maxTriggerConditions: 15,
  maxChecklistItems: 100,
  maxConditionsBranches: 10,
  minTotalBranches: 1,
  maxAdvancedLogicDepth: 2,
});

/**
 * Smallest value any limit may take. Every limit drives a gate that must
 * still allow one of whatever it counts, so a flag misconfigured to 0 (or a
 * negative, or a blank that coerces to one) would silently brick the builder
 * -- no actions addable, no branches, no conditions. Clamping trades an
 * unusable builder for one running at its tightest sane setting.
 */
export const LIMIT_FLOOR = 1;

/**
 * A flag value as a usable count: whole, at least LIMIT_FLOOR, and never NaN.
 * Infinity passes through -- an intentionally uncapped limit stays uncapped.
 */
const asCount = (value: number): number =>
  Number.isNaN(value) ? LIMIT_FLOOR : Math.max(LIMIT_FLOOR, Math.trunc(value));

/**
 * UCES feature-flag values for this app, synced from the React flag context
 * by UcesFlagsStoreSync (services/ucesFlagsClient.tsx) so that non-React
 * code -- notably the BranchesStore nesting gates -- can read them.
 *
 * Boolean defaults are false (the same fallback useUcesFlag uses), so every
 * gated capability stays off while the flags are loading or if loading fails.
 */
export class FeatureFlagsStore {
  /** `conditional_logic`: Conditional Logic blocks in the builder. */
  conditionalLogic = false;
  /**
   * `nested_loops`: nesting Advanced Logic -- loop in loop, Conditional
   * Logic in Conditional Logic, loop in Conditional Logic and Conditional
   * Logic in loop. Root-level loops/blocks are not affected by this flag.
   */
  nestedLoops = false;
  limits: AutomationLimits = { ...DEFAULT_AUTOMATION_LIMITS };

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setConditionalLogic(value: boolean): void {
    this.conditionalLogic = value;
  }

  setNestedLoops(value: boolean): void {
    this.nestedLoops = value;
  }

  setLimits(limits: AutomationLimits): void {
    this.limits = {
      maxActionsPerAutomation: asCount(limits.maxActionsPerAutomation),
      maxAutomationsPerSolution: asCount(limits.maxAutomationsPerSolution),
      maxTriggerConditions: asCount(limits.maxTriggerConditions),
      maxChecklistItems: asCount(limits.maxChecklistItems),
      maxConditionsBranches: asCount(limits.maxConditionsBranches),
      minTotalBranches: asCount(limits.minTotalBranches),
      maxAdvancedLogicDepth: asCount(limits.maxAdvancedLogicDepth),
    };
  }
}

// Module-level singleton (also exposed as rootStore.featureFlags): the
// candidate/branches stores are constructed without a RootStore reference,
// so they import this instance directly instead of reaching through the
// root store (which would be an import cycle).
export const featureFlagsStore = new FeatureFlagsStore();
